import { useEffect, useImperativeHandle, useRef, useState } from 'react'
import classes from './Game.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { CheckButton } from './components/CheckButton';
import { isCorrectKeySensitive } from './utils/functions';
import useFibConfig from './hooks/useFibConfig';
import { QUESTION_TYPE } from './utils/constants';
import clsx from 'clsx';
import { Fill, useTranslate, VisualFeedback } from '@educaplay/core';
import { DndGame } from './screens/DndGame';
import { WrittingGame } from './screens/WrittingGame';
import { motion, useAnimate } from 'framer-motion';
import { finishGame, hideBank, saveAnswer, setAnimating, skipQuestion } from "@educaplay/store/slices/gameSlice";
import { wait } from "./utils/promises";
import { generateGameState } from './utils/gameState';
import { DropdownGame } from './screens/DropdownGame';
import { SkipButton } from '@educaplay/core/components/SkipButton';
import { getFormattedAnswer, getTextCorrection, getUpdatedGameState } from './utils/userAnswer';
import useIsTouchDevice from "./hooks/useIsTouchDevice";

const dictionary = {
    [QUESTION_TYPE.DND]: DndGame,
    [QUESTION_TYPE.WRITTING]: WrittingGame,
    [QUESTION_TYPE.DROPDOWN]: DropdownGame
}

const TIME_TO_SKIP = 5000;

const Game = ({ onAnswer, ref }) => {
  const isTouchDevice = useIsTouchDevice();
  const { keySensitive } = useFibConfig();
  const currentIndex = useSelector(state => state.game.currentIndex);
  const dispatch = useDispatch();
  const isAnimating = useSelector(state => state.game.isAnimating);
  const visualFeedbackRef = useRef();
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [answeredBlanks, setAnsweredBlanks] = useState([]);
  const timerRef = useRef(null);
  
  const [containerCheckRef, animateButton] = useAnimate();
  const boardRef = useRef();
  const checkButtonRef = useRef();

  const t = useTranslate();

  const animateEntry = async () => {
    await boardRef.current?.entryAnimation();
    await animateButton(containerCheckRef.current, { y: 0 }, { duration: .5, type: "spring" });
  }

  useEffect(() => {
    setAnsweredBlanks([]);
  }, [currentIndex])

  const handleCheck = async () => {
    if (isAnimating) return;
    dispatch(setAnimating(true));
    if(!timerRef.current) {
      timerRef.current = setTimeout(() => {
        setShowSkipButton(true);
      }, TIME_TO_SKIP);
    }

    const alreadyCorrect = isWriting ?
      gameState.textBlanks?.filter(blank => blank.isCorrect).map(blank => blank.id)
    :
      gameState.idBlanks?.filter(blank => blank.isCorrect).map(blank => blank.id);
    
    let writtenWords = 
    gameState.textBlanks?.filter(blank => blank.userAnswer).map(blank => {
      const isCorrect = isCorrectKeySensitive(blank.expected, blank.userAnswer, keySensitive) ?? null;
      return {
        ...blank,
        isCorrect,
        tries: isCorrect ? blank.tries : blank.tries + 1
      }
    }) ?? [];

    const newIdBlanks = gameState.idBlanks?.map(blank => {
      if (blank.fill === null) {
        return blank
      }
      const userAnswer = isDropdownGame ?
        blank.fill
      :
        gameState.words.find(word => word.id === blank.fill)?.data ?? null;
      const isCorrect = isCorrectKeySensitive(blank.expected, userAnswer, keySensitive);
      return {
        ...blank,
        isCorrect,
        tries: isCorrect ? blank.tries : blank.tries + 1
      }
    }) ?? null;

    const newGameState = {
      ...gameState,
      textBlanks: gameState.textBlanks?.map(blank => {
        if(blank.userAnswer === null) {
          return blank
        }
        const isCorrect = isCorrectKeySensitive(blank.expected, blank.userAnswer, keySensitive);
        return {
          ...blank,
          isCorrect,
          tries: isCorrect ? blank.tries : blank.tries + 1
        }
      }) ?? null,
      idBlanks: newIdBlanks,
      words: gameState.words.map(word => {
        const blankWithWord = isWriting ? 
          writtenWords.find(written => written.userAnswer === word.data) ?? null
          :
          newIdBlanks.find(blank => blank.fill === word.id) ?? null;
          if (isWriting && blankWithWord) {
            const wordIndex = writtenWords.findIndex(writtenWord => writtenWord.userAnswer === word.data);
            writtenWords = writtenWords.slice(0, wordIndex).concat(writtenWords.slice(wordIndex + 1))
          }
        return {
          ...word,
          isCorrect: blankWithWord?.isCorrect ?? null
        }
      })
    }

    const textCorrection = isWriting ?
      newGameState.textBlanks?.reduce((acc, word) => {
        if (alreadyCorrect?.includes(word.id)) {
          return acc;
        }
        if(typeof word.isCorrect !== 'boolean') {
          acc[word.id] = 'empty';
        } else {
          acc[word.id] = word.isCorrect ? 'correct' : 'wrong';
        }
      
        return acc;
      }, {})
    :
      newGameState.idBlanks?.reduce((acc, word) => {
        if (alreadyCorrect?.includes(word.id)) {
          return acc;
        }
        if(typeof word.isCorrect !== 'boolean') {
          acc[word.id] = 'empty';
        } else {
          acc[word.id] = word.isCorrect ? 'correct' : 'wrong';
        }
      
        return acc;
      }, {});

    const isCorrect = isWriting ?
      newGameState.textBlanks?.every(blank => blank.isCorrect)
      :
      newGameState.idBlanks?.every(blank => blank.isCorrect);
    const duration = isCorrect ? 100 : 200;
    await visualFeedbackRef.current?.show({ isCorrect, duration });

    await wait(300);

    await boardRef.current?.checkAnimation(textCorrection);
    setGameState({
        ...newGameState,
        words: newGameState.words.map(word => {
          if(word.isCorrect === false) {
            return {
              ...word,
              isCorrect: null
            }
          }
          return word
        }),
        idBlanks: newGameState.idBlanks?.map(blank => {
          if (blank.isCorrect === false) {
              return {
                  ...blank,
                  isCorrect: null
              }
          }
          return blank
        }),
    })
    // setGameState(newGameState);
    
    await wait(300);
    dispatch(setAnimating(false));

    const formattedAnswer = 
    gameState.textBlanks?.reduce((acc, blank) => {
      if(blank.userAnswer === null) {
        acc[blank.id] = blank;
        return acc;
      }

      const isCorrect = isCorrectKeySensitive(blank.expected, blank.userAnswer, keySensitive);
      acc[blank.id] = {
        ...blank,
        tries: isCorrect ? blank.tries : blank.tries + 1,
        isCorrect
      }
      return acc;
    }, {})
    ??
    gameState.idBlanks?.reduce((acc, blank) => {
      if(blank.fill === null) {
        acc[blank.id] = blank;
        return acc;
      }

      const userAnswer = isDropdownGame ?
        blank.fill
      :
        gameState.words.find(word => word.id === blank.fill)?.data ?? null;

      const isCorrect = isCorrectKeySensitive(blank.expected, userAnswer, keySensitive);
      acc[blank.id] = {
        ...blank,
        userAnswer,
        tries: isCorrect ? blank.tries : blank.tries + 1,
        isCorrect
      }
      return acc;
    }, {});


    const formattedAnswerWithAnsweredBlanks = Object.entries(formattedAnswer).reduce((acc, blank) => {
      const [key, value] = blank;
      acc[key] = {
        ...value,
        answeredBlanks
      }
      return acc;
    }, {});

    onAnswer(formattedAnswerWithAnsweredBlanks, { 
        playErrorSound: false,
        showFeedback: false
    });

    const answeredBlanksIds = Object.values(formattedAnswer).filter(blank => blank.isCorrect).map(blank => blank.id);
    const uniqueAnsweredBlanks = [...new Set([...answeredBlanks, ...answeredBlanksIds])];
    setAnsweredBlanks(uniqueAnsweredBlanks);
  }

  useImperativeHandle(ref, () => ({
    getUserAnswer: () => {
      const newGameState = getUpdatedGameState({ gameState, gameType: question.questionType, keySensitive });
      const isCorrect = isWriting ?
        newGameState.textBlanks?.every(blank => blank.isCorrect)
        :
        newGameState.idBlanks?.every(blank => blank.isCorrect);

      const data = getFormattedAnswer({ gameState, gameType: question.questionType, keySensitive })
      const formattedAnswerWithAnsweredBlanks = Object.entries(data).reduce((acc, blank) => {
        const [key, value] = blank;
        acc[key] = {
          ...value,
          answeredBlanks
        }
        return acc;
      }, {});

      const points = question.calculatePoints(formattedAnswerWithAnsweredBlanks);
      
      return {
        data,
        isCorrect,
        points
      };
    },
    animateEntry,
    animateFail: async () => {
      const alreadyCorrect = isWriting ?
        gameState.textBlanks?.filter(blank => blank.isCorrect).map(blank => blank.id)
        :
        gameState.idBlanks?.filter(blank => blank.isCorrect).map(blank => blank.id);

      const newGameState = getUpdatedGameState({ gameState, gameType: question.questionType, keySensitive });

      const textCorrection = getTextCorrection({ alreadyCorrect, gameState: newGameState, gameType: question.questionType });

      await boardRef.current?.checkAnimation(textCorrection);
    }
  }));

  const numQuestions = useSelector(state => state.game.questions).length;
  const question = useSelector(state => state.game.questions[state.game.currentIndex]);
  const ScreenGame = dictionary[question.questionType];
  const isWriting = question.questionType === QUESTION_TYPE.WRITTING;
  const [hasFocus, setHasFocus] = useState(false);

  const [gameState, setGameState] = useState(() => generateGameState(question));

  useEffect(() => {
    setGameState(generateGameState(question));
  
    if(currentIndex > 0) {
      setTimeout(() => animateEntry());
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex])

  const isDropdownGame = question.questionType === QUESTION_TYPE.DROPDOWN;

  const handleGameCompleted = () => {
    checkButtonRef.current?.focus();
  }

  const numWords = gameState.words.length;
  const usedWords = gameState.words.filter(word => word.used).length;

  const changeScreenAnimation = async () => { 
    const hasMoreQuestions = currentIndex < numQuestions - 1;
    if (hasMoreQuestions) {
        await wait(400);
        animateEntry();
    }
}

  const handleClickPassButton = async() => {
    if (isAnimating) return;
    setShowSkipButton(false);
    timerRef.current = null; 

    const formattedAnswer = getFormattedAnswer({gameState, gameType: question.questionType, keySensitive});
    dispatch(saveAnswer(formattedAnswer, { 
        playErrorSound: false,
        showFeedback: false
    }));

    const action = currentIndex === numQuestions - 1 ? finishGame : skipQuestion;
    dispatch(action());
    changeScreenAnimation();
  }

  return (
    <>
      <Fill name={"core-bank-right"}>
        {t("specific.words")}: {usedWords}/{numWords}
      </Fill>

      <div className={clsx(classes.gameContainer,  (isDropdownGame || isWriting) && classes.topBank, (isTouchDevice && hasFocus) && classes.openKeyboard )}>
        <div className={clsx(classes.gameWrapper, isDropdownGame && classes.dropdownGame)}>
          <ScreenGame
            ref={boardRef}
            key={question.id}
            gameState={gameState}
            setGameState={setGameState}
            setHasFocus={inputHasFocus => { 
              if(isTouchDevice && isWriting) {
                dispatch(hideBank(inputHasFocus))  
              }
              setHasFocus(inputHasFocus);
            }}
            onCompleted={handleGameCompleted}
            animated
          />

          <motion.div 
            ref={containerCheckRef}
            initial={{ y: "200%", x: "-50%" }}
            className={clsx(classes.buttonContainer)}
          >
            <CheckButton
              ref={checkButtonRef}
              onClick={handleCheck}
              isBottomBank={question.questionType === QUESTION_TYPE.DND}
            />
          </motion.div>
        </div>
      </div>
        <SkipButton show={showSkipButton} onSkip={handleClickPassButton} isLastQuestion={currentIndex === numQuestions - 1} />
      <VisualFeedback ref={visualFeedbackRef} />
    </>
  )
}

export default Game